import {
  BTypeMainHead,
  BTypeCardView,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  colors,
  CTypeMap,
  BTypeBeneficiaryCTA,
  FullWidthButton,
  ATypeNationalMeritDescription,
  FullWidthTwinButton,
  call,
  GOI_WEB_URL,
  ATypeNationalMeritSummary,
  customEvent,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";
import facilities from "goi_common/src/constants/facilities_detail.json";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = facilities
    .filter((f) => targetSidos.includes(f.sido) && f.is_public)
    .map((f) => ({
      params: {
        companyname: f.companyname + " 소개",
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.companyname as string;
  const companyname = code.replace(" 소개", "");
  let p_facility;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    p_facility = facilities.find((f) => f.companyname === companyname);
    if (p_facility) {
      name = p_facility.companyname;

      district = p_facility.address.slice(0, 2);
      if (p_facility.address.split(" ")[0].length == 4)
        district = p_facility.address[0] + p_facility.address[2];

      description = `${p_facility.companyname} ${p_facility.address} 국가유공자 공설 장지 비용 안내, ${p_facility.companyname} ${p_facility.address} 국가유공자 공설 장지 우선 안치, ${p_facility.companyname} ${p_facility.address} 국가유공자 공설 장지 비용`;
    }
  }

  return {
    props: {
      defaultPFacility: p_facility,
      pageDescription: description,
      pageKeywords: `${p_facility?.companyname}, ${p_facility?.companyname} 국가유공자, ${district} 국가유공자 장례, ${district} 국가유공자 장례 비용, ${district} 국가유공자 화장 비용, ${district} 국가유공자 화장장, ${district} 국가유공자 장례 혜택`,
      pageOgTitle: `${p_facility?.companyname} 국가유공자 혜택 공설 장지 소개 및 비용 안내 :: 부산울산경남장례정보포털`,
      pageOgDescription: description,
      pageOgImage: p_facility?.images[0].url || "",
      hasError: !p_facility,
    },
  };
};

export default function companyname({
  defaultPFacility,
  pageOgTitle,
}: {
  defaultPFacility: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionPFacilities = facilities.filter((p) =>
    targetSidos.includes(String(p.sido)) && p.is_public
  );
  const sidoName = FUNERAL_SIDO.get(Number(defaultPFacility.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultPFacility.sido))?.get(
    Number(defaultPFacility.sigungu)
  );
  const carouselItems = regionPFacilities.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.gyeongsang-portal.co.kr/national/free-facility/${res.companyname} 소개/`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionPFacilities.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  return (
    <Stack>
      <Head>
        <title>{pageOgTitle}</title>
        {regionPFacilities.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>

      <BTypeMainHead
        sidoName={sidoName}
        sigunguName={sigunguName}
        mainColor={regionColor}
        defaultParlor={defaultPFacility}
        title={`${defaultPFacility.companyname}`}
        managed_type={defaultPFacility.managed_type}
        isJangji
        event={{
          domain: "gyeongsang",
          path: "nat_fr_facility",
          trigger_and_target: "br_crumb_facility",
        }}
      />

      <Stack
        margin="20px 12px"
        padding="24px 16px 10px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
        gap="12px"
      >
        <Box className="subtitle_21_b">
          {defaultPFacility.companyname}
          <br />
          국가유공자 장례 준비
        </Box>
        <ATypeNationalMeritDescription />
        <Box className="subtitle_14_b" textAlign="center">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              call("1666-9784");
              customEvent({
                domain: "gyeongsang",
                path: "nat_fr_facility",
                trigger_and_target: "goi_call",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
            onClick: () => {
              customEvent({
                domain: "gyeongsang",
                path: "nat_fr_facility",
                trigger_and_target: "uv_mth_req",
              });
            },
          }}
          backgroundColor={regionColor.c400}
        />
      </Stack>

      <Stack
        margin="20px 12px"
        padding="24px 16px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
        gap="12px"
      >
        <ATypeNationalMeritSummary mainColor={regionColor} />
        <Box className="subtitle_14_b" textAlign="center">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              call("1666-9784");
              customEvent({
                domain: "gyeongsang",
                path: "nat_fr_facility",
                trigger_and_target: "goi_call_btom",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
            onClick: () => {
              customEvent({
                domain: "gyeongsang",
                path: "nat_fr_facility",
                trigger_and_target: "uv_mth_req_btom",
              });
            },
          }}
          backgroundColor={regionColor.c400}
        />
      </Stack>

      <Stack
        margin="20px 12px"
        padding="24px 16px 20px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
        gap="12px"
      >
        <CTypeMap
          defaultParlor={defaultPFacility}
          address={defaultPFacility.address}
        />
      </Stack>

      <Stack
        margin="20px 12px"
        padding="24px 16px 10px"
        style={{
          background: "white",
          boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
        }}
        gap="12px"
      >
        <h3 className="subtitle_18_b">부산 울산 경남 국가유공자 장지</h3>
        {regionPFacilities.map((res) => (
          <BTypeCardView
            descriptions={["국가유공자 무료"]}
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/national/free-facility/${res.companyname} 소개/`}
            imgSrc={res.images[0]?.url ? res.images[0].url : ""}
            event={{
              domain: "gyeongsang",
              path: "nat_fr_facility",
              trigger_and_target: "uv_fr_facility_detail",
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}
